import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTranslatedNavigationSections, hasPermission } from '@shared/config/navigation';
import { useTheme } from '@shared/contexts/ThemeContext';
import { useImpersonation } from '@shared/contexts/ImpersonationContext';
import { useNotifications } from '@shared/contexts/NotificationContext';
import { getToken } from '@shared/services/api';
import { focusRing } from '@shared/utils/statusTone';
import * as Icons from 'lucide-react';
import PgkbLogo from './PgkbLogo';
import EnvironmentChip from './EnvironmentChip';
import TrialBadge from './TrialBadge';
import LanguageSwitcher from './LanguageSwitcher';

interface User {
  id: string;
  email: string;
  full_name: string;
  email_verified: boolean;
  role?: string;
  is_platform_admin?: boolean;
  is_suspended?: boolean;
}

interface SidebarProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function Sidebar({
  user,
  isOpen,
  onClose,
  onLogout,
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { isImpersonating, clearImpersonationState } = useImpersonation();
  const { unreadCount } = useNotifications();
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  // Get translated navigation sections
  const navigationSections = useMemo(() => getTranslatedNavigationSections(t), [t]);

  const translateRole = (role?: string) => {
    if (!role) return t('userMenu.user');

    const normalizedRole = role.toLowerCase();
    if (normalizedRole === 'platform_admin') {
      return t('admin:platform_administrator', { defaultValue: role });
    }

    return t(`settings:team.roles.${normalizedRole}`, { defaultValue: role });
  };

  const handleStopImpersonating = async () => {
    if (!isImpersonating) return;
    setStoppingImpersonation(true);
    try {
      const token = getToken();
      const response = await fetch('/api/v1/admin/stop-impersonating', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('auth_token', data.token);
        clearImpersonationState();
        window.location.href = '/admin/customers';
      }
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
    } finally {
      setStoppingImpersonation(false);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`
        fixed inset-y-0 left-0 z-50 bg-surface-2-light dark:bg-surface-2-dark border-r border-surface-border-light dark:border-surface-border-dark transform transition-all duration-300 ease-in-out flex flex-col
        ${isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
        ${collapsed ? 'lg:w-20' : 'lg:w-[228px]'} w-[228px]
        lg:translate-x-0 lg:relative lg:inset-0 lg:transform-none lg:shadow-none lg:h-full
      `}
        aria-label="Sidebar"
      >
        {/* Collapse Button - Inside Sidebar on Right Margin */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className={`hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-3 z-10 p-1.5 rounded-full bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark shadow-sm hover:shadow-md hover:bg-surface-2-light dark:hover:bg-surface-2-dark hover:scale-110 transition-all ${focusRing}`}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            {collapsed ? <Icons.ChevronRight size={14} /> : <Icons.ChevronLeft size={14} />}
          </button>
        )}

        {/* Sidebar Header — brand */}
        <div className={`${collapsed ? 'px-0 pt-4 pb-2 flex justify-center' : 'px-2 pt-3.5 pb-2'}`}>
          <div className="flex items-start justify-between gap-2">
            <button
              onClick={() => {
                navigate('/dashboard');
                onClose();
              }}
              className={`flex items-center gap-2 rounded-lg transition-colors hover:opacity-80 ${focusRing}`}
              title="pgkb"
            >
              <PgkbLogo size="sm" showText={!collapsed} />
            </button>
            {!collapsed && (
              <button
                type="button"
                onClick={onClose}
                className={`lg:hidden inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-text-muted-light transition-colors hover:bg-surface-card-light hover:text-slate-900 dark:text-text-muted-dark dark:hover:bg-surface-card-dark dark:hover:text-white ${focusRing}`}
                aria-label={t('nav.closeMenu')}
                title={t('nav.closeMenu')}
              >
                <Icons.X size={18} aria-hidden="true" />
              </button>
            )}
          </div>
          {!collapsed && (
            <h1
              className="mt-2.5 px-1 text-[11px] font-semibold leading-tight whitespace-normal break-words text-text-subtle-light dark:text-text-subtle-dark"
              title={t('sidebar.title')}
            >
              {t('sidebar.title')}
            </h1>
          )}
        </div>

        {/* Environment chip + trial status */}
        {!collapsed && (
          <div className="px-2">
            <EnvironmentChip />
            <div className="mb-3 flex justify-start">
              <TrialBadge />
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {navigationSections
            .map((section) => ({
              ...section,
              visibleItems: section.items.filter((item) => hasPermission(item, user)),
            }))
            .filter((section) => section.visibleItems.length > 0)
            .map((section) => (
              <div key={section.title} className={`${collapsed ? 'px-2 py-1' : 'px-2 py-1.5'}`}>
                {!collapsed && (
                  <h3
                    className="px-2 pt-3 pb-1.5 text-[10.5px] font-semibold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.06em] truncate"
                    title={section.title}
                  >
                    {section.title}
                  </h3>
                )}
                <ul className={`${collapsed ? 'space-y-0' : 'space-y-0.5'}`}>
                  {section.visibleItems.map((item) => {
                    const isActive = isActivePath(item.path);
                    return (
                      <li key={item.path}>
                        <button
                          onClick={() => {
                            navigate(item.path);
                            onClose();
                          }}
                          aria-current={isActive ? 'page' : undefined}
                          className={`
                            relative w-full flex items-center rounded-lg transition-colors group ${focusRing}
                            ${collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-2 py-1.5 text-[12.5px]'}
                            ${
                              isActive
                                ? 'bg-accent-soft-light dark:bg-accent-soft-dark text-accent-text-light dark:text-accent-text-dark font-semibold'
                                : 'text-text-muted-light dark:text-text-muted-dark hover:bg-surface-card-light dark:hover:bg-surface-card-dark hover:text-slate-900 dark:hover:text-white'
                            }
                          `}
                          title={item.label}
                        >
                          {isActive && (
                            <span
                              className="absolute left-0 top-0 bottom-0 w-0.5 rounded-r bg-brand-600 dark:bg-brand-400"
                              aria-hidden="true"
                            />
                          )}
                          <span className="flex-shrink-0">
                            <DynamicIcon
                              name={item.icon}
                              size={15}
                              strokeWidth={isActive ? 2.5 : 2}
                            />
                          </span>
                          {!collapsed && (
                            <span className="min-w-0 whitespace-normal break-words text-left leading-tight">
                              {item.label}
                            </span>
                          )}
                          {!collapsed && isActive && (
                            <span
                              className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-600 dark:bg-brand-400 flex-shrink-0"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </div>

        {/* User & Footer Section */}
        <div
          className={`border-t border-surface-border-light dark:border-surface-border-dark ${collapsed ? 'p-2' : 'p-3 space-y-3'}`}
        >
          {!collapsed && (
            <>
              {/* Footer — one surface: actions, language, then the user card. Three
                  separate floating boxes read as unrelated panels and the language
                  trigger floated unanchored between them. */}
              <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark">
                <div className="flex flex-col items-center gap-1 px-1.5 py-1.5">
                  <div className="flex w-full items-center justify-around">
                    <ActionIconButton
                      icon={<Icons.Bell size={16} />}
                      label={t('header.notifications')}
                      onClick={() => navigate('/notifications')}
                      badge={unreadCount > 0}
                    />
                    <ActionIconButton
                      icon={theme === 'dark' ? <Icons.Sun size={16} /> : <Icons.Moon size={16} />}
                      label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
                      onClick={toggleTheme}
                    />
                    <ActionIconButton
                      icon={<Icons.User size={16} />}
                      label={t('nav.profile')}
                      onClick={() => navigate('/profile')}
                    />
                    <ActionIconButton
                      icon={<Icons.LogOut size={16} />}
                      label={t('nav.logout')}
                      onClick={onLogout}
                      color="text-state-danger-light dark:text-state-danger-dark"
                    />
                  </div>
                  <LanguageSwitcher
                    variant="minimal"
                    placement="top"
                    className="flex w-full justify-center"
                  />
                </div>

                <div className="h-px bg-surface-border-light dark:bg-surface-border-dark" />

                {/* User card */}
                <button
                  onClick={() => navigate('/profile')}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors hover:bg-surface-2-light dark:hover:bg-surface-2-dark ${focusRing}`}
                >
                  <div className="w-7 h-7 flex-shrink-0 bg-brand-600 dark:bg-brand-400 rounded-full flex items-center justify-center text-on-accent-light dark:text-on-accent-dark font-bold text-xs">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p
                      className="text-xs font-semibold text-slate-900 dark:text-white truncate leading-tight"
                      title={user?.full_name}
                    >
                      {user?.full_name}
                    </p>
                    <p
                      className="text-[10.5px] font-medium text-text-subtle-light dark:text-text-subtle-dark whitespace-normal break-words leading-tight mt-0.5"
                      title={translateRole(user?.role)}
                    >
                      {translateRole(user?.role)}
                    </p>
                  </div>
                </button>
              </div>

              {isImpersonating && (
                <button
                  onClick={handleStopImpersonating}
                  disabled={stoppingImpersonation}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 bg-state-warn-light dark:bg-state-warn-dark text-white dark:text-on-accent-dark rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all disabled:opacity-50 ${focusRing}`}
                >
                  <Icons.ShieldAlert size={14} />
                  <span>{t('sidebar.stopImpersonating')}</span>
                </button>
              )}
            </>
          )}
          {collapsed && (
            <div className="flex flex-col items-center space-y-2">
              <button
                onClick={() => navigate('/notifications')}
                className={`min-h-11 min-w-11 p-2 rounded-lg hover:bg-surface-card-light dark:hover:bg-surface-card-dark transition-colors relative ${focusRing}`}
                aria-label={t('header.notifications')}
              >
                <Icons.Bell size={18} className="text-text-muted-light dark:text-text-muted-dark" />
                {unreadCount > 0 && (
                  <div className="absolute top-1.5 right-1.5 w-2 h-2 bg-state-danger-light dark:bg-state-danger-dark rounded-full" />
                )}
              </button>
              <button
                onClick={toggleTheme}
                className={`min-h-11 min-w-11 p-2 rounded-lg hover:bg-surface-card-light dark:hover:bg-surface-card-dark transition-colors ${focusRing}`}
                aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
              >
                {theme === 'dark' ? (
                  <Icons.Sun
                    size={18}
                    className="text-text-muted-light dark:text-text-muted-dark"
                  />
                ) : (
                  <Icons.Moon
                    size={18}
                    className="text-text-muted-light dark:text-text-muted-dark"
                  />
                )}
              </button>
              <button
                onClick={() => navigate('/profile')}
                className={`min-h-11 min-w-11 p-2 rounded-lg hover:bg-surface-card-light dark:hover:bg-surface-card-dark transition-colors ${focusRing}`}
                aria-label={t('nav.profile')}
              >
                <Icons.User size={18} className="text-text-muted-light dark:text-text-muted-dark" />
              </button>
              <div className="w-7 h-7 bg-brand-600 dark:bg-brand-400 rounded-full flex items-center justify-center text-on-accent-light dark:text-on-accent-dark font-bold text-xs">
                {user?.full_name?.charAt(0).toUpperCase()}
              </div>
              {isImpersonating && (
                <button
                  onClick={handleStopImpersonating}
                  disabled={stoppingImpersonation}
                  className={`min-h-11 min-w-11 p-2 rounded-lg hover:bg-state-warn-light/10 transition-colors disabled:opacity-50 ${focusRing}`}
                  aria-label={t('sidebar.stopImpersonating')}
                >
                  <Icons.ShieldAlert
                    size={18}
                    className="text-state-warn-light dark:text-state-warn-dark"
                  />
                </button>
              )}
              <button
                onClick={onLogout}
                className={`min-h-11 min-w-11 p-2 rounded-lg hover:bg-state-danger-light/10 transition-colors ${focusRing}`}
                aria-label={t('nav.logout')}
              >
                <Icons.LogOut
                  size={18}
                  className="text-state-danger-light dark:text-state-danger-dark"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DynamicIcon({
  name,
  size,
  strokeWidth,
}: {
  name?: string;
  size: number;
  strokeWidth: number;
}) {
  // Map of emoji to Lucide icon names if they haven't been updated yet
  const emojiMap: Record<string, string> = {
    '📊': 'LayoutDashboard',
    '💾': 'Database',
    '⚙️': 'Settings',
    '📅': 'Calendar',
    '🖥️': 'Monitor',
    '👥': 'Users',
    '📦': 'Package',
    '🏢': 'Building2',
    '👤': 'User',
    '🔑': 'Key',
    '🔐': 'Lock',
    '📈': 'TrendingUp',
    '📋': 'ClipboardList',
    '🔧': 'Wrench',
    '🗑️': 'Trash2',
    '🚨': 'AlertTriangle',
    '📍': 'MapPin',
    '📄': 'FileText',
    '🔔': 'Bell',
    '🔗': 'Link',
    '🧾': 'Receipt',
    '💳': 'CreditCard',
    '🏛️': 'Library',
    '🏥': 'Activity',
    '🔌': 'Plug',
    '🔒': 'ShieldCheck',
    '🚪': 'LogOut',
  };

  const iconName = (name && emojiMap[name]) || name || 'Circle';
  const Icon = (Icons as unknown as Record<string, Icons.LucideIcon>)[iconName] ?? Icons.Circle;
  // Lucide's namespace is not indexable in its public types; the cast above keeps the
  // dynamic-config lookup (navigation.ts stores icon names as strings) type-safe downstream.
  return <Icon size={size} strokeWidth={strokeWidth} />;
}

function ActionIconButton({
  icon,
  onClick,
  badge,
  label,
  color = 'text-text-muted-light dark:text-text-muted-dark',
}: {
  icon: React.ReactNode;
  onClick: () => void;
  badge?: boolean;
  label: string;
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`min-h-11 min-w-11 p-2 rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors relative ${color} ${focusRing}`}
    >
      {icon}
      {badge && (
        <div className="absolute top-1.5 right-1.5 w-2 h-2 bg-state-danger-light dark:bg-state-danger-dark rounded-full" />
      )}
    </button>
  );
}
